import { UserManager, WebStorageStateStore, type User } from "oidc-client-ts";

export const authCallbackPath = "/auth/callback";

export interface SignedInUser {
  userName: string;
  email: string | null;
}

export interface AuthService {
  getSignedInUser(): Promise<SignedInUser | null>;
  getAccessToken(): Promise<string | null>;
  signIn(returnPath: string): Promise<void>;
  completeSignIn(): Promise<string>;
  signOut(): Promise<void>;
}

/**
 * OpenIddict authorization code + PKCE through oidc-client-ts; refresh tokens renew the access token silently.
 * The authority is the API origin: the page itself in production, the API host behind the Vite dev server.
 */
export function createAuthService(origin: string, authority: string): AuthService {
  let userManager: UserManager | null = null;
  let signInCompletion: Promise<string> | null = null;

  const getUserManager = (): UserManager => {
    userManager ??= new UserManager({
      authority,
      client_id: "mr-portfolio-spa",
      redirect_uri: `${origin}${authCallbackPath}`,
      post_logout_redirect_uri: `${origin}/`,
      response_type: "code",
      scope: "openid email profile offline_access portfolio_api",
      automaticSilentRenew: true,
      userStore: new WebStorageStateStore({ store: window.sessionStorage }),
    });

    return userManager;
  };

  const getValidUser = async (): Promise<User | null> => {
    const storedUser = await getUserManager().getUser();

    if (storedUser === null) {
      return null;
    }

    if (!storedUser.expired) {
      return storedUser;
    }

    try {
      const renewedUser = await getUserManager().signinSilent();

      return renewedUser;
    } catch {
      await getUserManager().removeUser();

      return null;
    }
  };

  const authService: AuthService = {
    async getSignedInUser() {
      const validUser = await getValidUser();
      const signedInUser = validUser === null ? null : toSignedInUser(validUser);

      return signedInUser;
    },
    async getAccessToken() {
      const validUser = await getValidUser();
      const accessToken = validUser?.access_token ?? null;

      return accessToken;
    },
    async signIn(returnPath) {
      await getUserManager().signinRedirect({ state: returnPath });
    },
    completeSignIn() {
      signInCompletion ??= getUserManager()
        .signinRedirectCallback()
        .then((signedInUser) => toReturnPath(signedInUser.state));

      return signInCompletion;
    },
    async signOut() {
      await getUserManager().signoutRedirect();
    },
  };

  return authService;
}

export const authService = createAuthService(window.location.origin, import.meta.env.VITE_AUTH_AUTHORITY || window.location.origin);

function toReturnPath(state: unknown): string {
  const isLocalPath = typeof state === "string" && state.startsWith("/") && !state.startsWith("//");
  const returnPath = isLocalPath ? state : "/";

  return returnPath;
}

function toSignedInUser(user: User): SignedInUser {
  const signedInUser: SignedInUser = {
    userName: user.profile.name ?? user.profile.email ?? user.profile.sub,
    email: user.profile.email ?? null,
  };

  return signedInUser;
}
